import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutGrid, List, Kanban, Search, SlidersHorizontal, X as XIcon } from 'lucide-react';
import { format } from 'date-fns';
import { api } from '../api/client';
import { Badge } from '../components/ui/Badge';
import { PrioridadeBadge } from '../components/demandas/PrioridadeBadge';
import { DemandaCard } from '../components/demandas/DemandaCard';
import { KanbanBoard } from '../components/demandas/KanbanBoard';
import { EmptyState } from '../components/ui/EmptyState';
import { ETAPA_MAP, ETAPAS, PRIORIDADES, CANAIS } from '../lib/constants';
import { parseDate, formatPeriodo } from '../lib/dates';

const ORDEM_OPTS = [
  { key: 'recentes', label: 'Atualizadas recentemente' },
  { key: 'criadas', label: 'Mais recentes (criação)' },
  { key: 'antigas', label: 'Mais antigas' },
  { key: 'acao', label: 'Próxima data de ação' },
  { key: 'postagem', label: 'Próxima data de postagem' },
  { key: 'prioridade', label: 'Prioridade (urgente primeiro)' },
];

const AGRUPAMENTOS = [
  { key: 'none', label: 'Sem agrupamento' },
  { key: 'etapa', label: 'Por etapa' },
  { key: 'secretaria', label: 'Por secretaria' },
  { key: 'responsavel', label: 'Por responsável' },
  { key: 'prioridade', label: 'Por prioridade' },
];

export function ProducaoPage() {
  const nav = useNavigate();
  // Default = lista (1 demanda por linha) porque é a forma mais densa pra
  // varrer todas as demandas. Cards e Kanban ficam como alternativas.
  const [view, setView] = useState(localStorage.getItem('demandas.view') || 'lista');
  const [demandas, setDemandas] = useState([]);
  const [secretarias, setSecretarias] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [agrupar, setAgrupar] = useState('none');

  const [filtros, setFiltros] = useState({
    q: '',
    etapa: '',
    secretaria_id: '',
    prioridade: '',
    responsavel_id: '',
    canal: '',
    de: '',
    ate: '',
    order: 'recentes',
  });
  const [incluirCanceladas, setIncluirCanceladas] = useState(
    () => localStorage.getItem('demandas.incluirCanceladas') === 'true'
  );
  // Pedido da Laila: filtrar só o que está pendente, ocultando as já publicadas
  // (que poluíam a lista de 200+ demandas). Persistido por navegador.
  const [ocultarPublicadas, setOcultarPublicadas] = useState(
    () => localStorage.getItem('demandas.ocultarPublicadas') === 'true'
  );

  useEffect(() => { localStorage.setItem('demandas.view', view); }, [view]);
  useEffect(() => { localStorage.setItem('demandas.incluirCanceladas', String(incluirCanceladas)); }, [incluirCanceladas]);
  useEffect(() => { localStorage.setItem('demandas.ocultarPublicadas', String(ocultarPublicadas)); }, [ocultarPublicadas]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filtros).forEach(([k, v]) => { if (v) params.set(k, v); });
      if (incluirCanceladas) params.set('incluir_canceladas', 'true');
      if (ocultarPublicadas) params.set('ocultar_publicadas', 'true');
      const res = await api.get(`/demandas?${params.toString()}`);
      setDemandas(res.demandas);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [
    filtros.etapa, filtros.secretaria_id, filtros.prioridade, filtros.responsavel_id,
    filtros.canal, filtros.de, filtros.ate, filtros.order, incluirCanceladas, ocultarPublicadas,
  ]);

  useEffect(() => {
    Promise.all([api.get('/secretarias'), api.get('/usuarios').catch(() => ({ usuarios: [] }))])
      .then(([s, u]) => {
        setSecretarias(s.secretarias);
        setResponsaveis((u.usuarios || []).filter((x) => x.perfil !== 'secretario' && x.ativo));
      });
  }, []);

  const onSearch = (e) => { e.preventDefault(); load(); };

  const limpar = () => setFiltros({ q: '', etapa: '', secretaria_id: '', prioridade: '', responsavel_id: '', canal: '', de: '', ate: '', order: 'recentes' });
  const temFiltroAtivo = Object.entries(filtros).some(([k, v]) => v && k !== 'order');

  const grupos = useMemo(() => {
    if (agrupar === 'none') return [{ key: 'todas', label: null, itens: demandas }];
    const map = new Map();
    for (const d of demandas) {
      let key = 'outros', label = 'Outros';
      if (agrupar === 'etapa') { key = d.etapa; label = ETAPA_MAP[d.etapa]?.label || d.etapa; }
      if (agrupar === 'secretaria') { key = d.secretaria_id; label = d.secretaria_nome; }
      if (agrupar === 'responsavel') { key = d.responsavel_id || 'sem'; label = d.responsavel_nome || 'Sem responsável'; }
      if (agrupar === 'prioridade') { key = d.prioridade; label = PRIORIDADES.find((p) => p.key === d.prioridade)?.label || d.prioridade; }
      if (!map.has(key)) map.set(key, { key, label, itens: [] });
      map.get(key).itens.push(d);
    }
    return [...map.values()];
  }, [demandas, agrupar]);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Demandas</h1>
          <p className="page-subtitle">
            {demandas.length} demanda{demandas.length === 1 ? '' : 's'} encontrada{demandas.length === 1 ? '' : 's'}
            {ocultarPublicadas && <span className="muted"> · publicadas ocultas</span>}
          </p>
        </div>
        <div className="row-wrap" style={{ gap: 8 }}>
          <div style={{ display: 'flex', background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderRadius: 10, padding: 3 }}>
            {[
              { k: 'cards', Icon: LayoutGrid, title: 'Cards' },
              { k: 'lista', Icon: List, title: 'Lista' },
              { k: 'kanban', Icon: Kanban, title: 'Kanban' },
            ].map(({ k, Icon, title }) => (
              <button
                key={k}
                onClick={() => setView(k)}
                title={title}
                className={`btn btn-sm ${view === k ? 'btn-primary' : 'btn-ghost'}`}
                style={{ minHeight: 32, padding: '4px 10px' }}
              ><Icon size={14} /></button>
            ))}
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => setFiltrosAbertos((v) => !v)}>
            <SlidersHorizontal size={14} /> Filtros{(temFiltroAtivo || ocultarPublicadas || incluirCanceladas) && ' ·'}
          </button>
        </div>
      </div>

      <form className="row-wrap" style={{ marginBottom: 12, gap: 8 }} onSubmit={onSearch}>
        <div style={{ position: 'relative', flex: '1 1 260px', minWidth: 200 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-text-muted)' }} />
          <input className="input" style={{ paddingLeft: 36 }} placeholder="Buscar por título, objetivo ou número..." value={filtros.q} onChange={(e) => setFiltros((f) => ({ ...f, q: e.target.value }))} />
        </div>
        <select className="select" style={{ maxWidth: 260 }} value={filtros.order} onChange={(e) => setFiltros((f) => ({ ...f, order: e.target.value }))}>
          {ORDEM_OPTS.map((o) => <option key={o.key} value={o.key}>Ordenar: {o.label}</option>)}
        </select>
        <select className="select" style={{ maxWidth: 240 }} value={agrupar} onChange={(e) => setAgrupar(e.target.value)}>
          {AGRUPAMENTOS.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
        </select>
        <button className="btn btn-secondary btn-sm" type="submit">Buscar</button>
      </form>

      {filtrosAbertos && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="card-body">
            <div className="grid-3" style={{ gap: 12 }}>
              <div className="field">
                <label>Etapa</label>
                <select className="select" value={filtros.etapa} onChange={(e) => setFiltros((f) => ({ ...f, etapa: e.target.value }))}>
                  <option value="">Todas</option>
                  {ETAPAS.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Secretaria</label>
                <select className="select" value={filtros.secretaria_id} onChange={(e) => setFiltros((f) => ({ ...f, secretaria_id: e.target.value }))}>
                  <option value="">Todas</option>
                  {secretarias.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Prioridade</label>
                <select className="select" value={filtros.prioridade} onChange={(e) => setFiltros((f) => ({ ...f, prioridade: e.target.value }))}>
                  <option value="">Todas</option>
                  {PRIORIDADES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Responsável</label>
                <select className="select" value={filtros.responsavel_id} onChange={(e) => setFiltros((f) => ({ ...f, responsavel_id: e.target.value }))}>
                  <option value="">Qualquer um</option>
                  {responsaveis.map((r) => <option key={r.id} value={r.id}>{r.nome}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Canal</label>
                <select className="select" value={filtros.canal} onChange={(e) => setFiltros((f) => ({ ...f, canal: e.target.value }))}>
                  <option value="">Qualquer</option>
                  {CANAIS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Período (ação/postagem)</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input type="date" className="input" value={filtros.de} onChange={(e) => setFiltros((f) => ({ ...f, de: e.target.value }))} />
                  <input type="date" className="input" value={filtros.ate} onChange={(e) => setFiltros((f) => ({ ...f, ate: e.target.value }))} />
                </div>
              </div>
            </div>
            <div className="row" style={{ justifyContent: 'space-between', marginTop: 12, alignItems: 'center' }}>
              <div className="row-wrap" style={{ gap: 16 }}>
                <label className="row" style={{ gap: 6, fontSize: 13, color: 'var(--c-text-muted)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={ocultarPublicadas}
                    onChange={(e) => setOcultarPublicadas(e.target.checked)}
                  />
                  Ocultar publicadas
                </label>
                <label className="row" style={{ gap: 6, fontSize: 13, color: 'var(--c-text-muted)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={incluirCanceladas}
                    onChange={(e) => setIncluirCanceladas(e.target.checked)}
                  />
                  Mostrar canceladas
                </label>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={limpar}><XIcon size={13} /> Limpar filtros</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loader-full"><span className="spinner" /></div>
      ) : demandas.length === 0 ? (
        <div className="card"><div className="card-body"><EmptyState title="Nenhuma demanda encontrada" description="Ajuste os filtros ou aguarde novas demandas." /></div></div>
      ) : view === 'kanban' ? (
        <KanbanBoard demandas={demandas} onChange={load} />
      ) : view === 'lista' ? (
        <div className="stack" style={{ gap: 18 }}>
          {grupos.map((g) => (
            <div key={g.key}>
              {g.label && <h3 style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--c-text-muted)', margin: '4px 0 8px' }}>{g.label} <span style={{ color: 'var(--c-text-soft)' }}>· {g.itens.length}</span></h3>}
              <div className="card"><div className="card-body" style={{ padding: 0 }}>
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr><th></th><th>Nº</th><th>Título</th><th>Secretaria</th><th>Etapa</th><th>Responsável</th><th>Data ação</th><th>Atualizada</th></tr>
                    </thead>
                    <tbody>
                      {g.itens.map((d) => (
                        <tr key={d.id} style={{ cursor: 'pointer' }} onClick={() => nav(`/demandas/${d.codigo}`)}>
                          <td style={{ width: 4, padding: 0, background: PRIORIDADES.find((p) => p.key === d.prioridade)?.dotColor }}></td>
                          <td className="text-xs muted">#{d.id}</td>
                          <td><strong>{d.titulo}</strong>{d.nao_lidas > 0 && <Badge color="primary" style={{ marginLeft: 6 }}>{d.nao_lidas}</Badge>}</td>
                          <td className="text-sm">{d.secretaria_nome}</td>
                          <td><Badge color={ETAPA_MAP[d.etapa]?.color}>{ETAPA_MAP[d.etapa]?.label}</Badge></td>
                          <td className="text-sm">{d.responsavel_nome || <span className="muted">—</span>}</td>
                          <td>{d.data_acao ? formatPeriodo(d.data_acao, d.data_acao_fim) : <span className="muted">—</span>}</td>
                          <td className="text-xs muted">{format(new Date(d.updated_at), 'dd/MM HH:mm')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="stack" style={{ gap: 18 }}>
          {grupos.map((g) => (
            <div key={g.key}>
              {g.label && <h3 style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--c-text-muted)', margin: '4px 0 8px' }}>{g.label} <span style={{ color: 'var(--c-text-soft)' }}>· {g.itens.length}</span></h3>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                {g.itens.map((d) => <DemandaCard key={d.id} demanda={d} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
